// ─────────────────────────────────────────────────────────────────────────────
// AIR DUCT TX — the two PROCESS diagrams.
//
// Erez, 2026-07-26: the lanes version did not make sense here, and he was right.
// LANES explains a SYSTEM as columns of parts. Air Duct's story is a PROCESS —
// a job travelling left to right — and forcing it into three vertical lanes hid
// the one thing that matters. skill://diagrams law 2 says the visual structure
// must match the REAL structure; a process has a spine, so this has a spine.
//
// The layout is therefore deliberately different from lanes.tsx:
//   ROW A  the systems that serve each step        (above the spine)
//   ROW B  THE SPINE — the eight steps of a job    (the middle, biggest)
//   ROW C  what goes wrong at that step            (below the spine)
//   ROW D  (diagram 2 only) everything we would add (below again, in green)
//
// THE COLOUR RULE (Erez): diagram 1 is ENTIRELY BLUE — all of it is theirs.
// Diagram 2 is the identical blue, untouched, PLUS GREEN, and green is only ever
// Tailormind. So "what is new" needs no legend: it is every green thing.
//
// Everything traces to meetings 89/90. Unknowns are drawn as unknowns.
// ─────────────────────────────────────────────────────────────────────────────

import type { Node, Edge, NodeProps } from "@xyflow/react";
import { Handle, Position, MarkerType } from "@xyflow/react";
import { PAD, INNER, LABEL_BLOCK, DiagramFrame } from "./lanes";

const F = 'ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';
const MONO = '"SFMono-Regular", Menlo, Consolas, "PT Mono", Courier, monospace';

const Ports = () => (<>
  <Handle type="target" id="l" position={Position.Left} style={{ opacity: 0, width: 1, height: 1 }} />
  <Handle type="source" id="r" position={Position.Right} style={{ opacity: 0, width: 1, height: 1 }} />
  <Handle type="target" id="t" position={Position.Top} style={{ opacity: 0, width: 1, height: 1 }} />
  <Handle type="source" id="b" position={Position.Bottom} style={{ opacity: 0, width: 1, height: 1 }} />
</>);

// Two families only. Blue = theirs. Green = ours. Nothing else carries meaning.
// Notion palette (Erez, 2026-07-28). Blue still means theirs, green still means
// ours — only the pigment changed.
const C = {
  ink: "#37352F", ink2: "#787774", ink3: "#9B9A97",
  blueDeep: "#0B6E99", blueMid: "#E7F3F8", blueSoft: "#F4F9FB", blueEdge: "#C9DDE8",
  greenDeep: "#0F7B6C", greenMid: "#EDF3EC", greenSoft: "#F5F9F4", greenEdge: "#C3DCC8",
  none: "#F1F1EF", noneInk: "#9B9A97",
};

/** A step on the spine — the thing that actually happens to a job. */
function Step({ data }: NodeProps) {
  const d = data as unknown as { n: string; label: string; w: number; ours?: boolean };
  return (
    <div data-zoom style={{ width: d.w, height: 104, borderRadius: 3, boxSizing: "border-box",
      background: d.ours ? C.greenDeep : C.blueDeep, color: "#fff", fontFamily: F,
      padding: "14px 16px", display: "flex", flexDirection: "column", justifyContent: "center", gap: 4 }}>
      <div style={{ fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: ".16em", opacity: .62 }}>{d.n}</div>
      <div style={{ fontSize: 19, fontWeight: 650, letterSpacing: "-.01em", lineHeight: 1.15 }}>{d.label}</div>
      <Ports />
    </div>
  );
}

/** A system that serves a step. Blue = theirs, green = ours, grey = nothing there. */
function Sys({ data }: NodeProps) {
  const d = data as unknown as { label: string; sub?: string; w: number; kind?: "their" | "ours" | "none" };
  const k = d.kind ?? "their";
  const bg = k === "ours" ? C.greenMid : k === "none" ? C.none : C.blueMid;
  const ink = k === "ours" ? C.greenDeep : k === "none" ? C.noneInk : C.blueDeep;
  return (
    <div data-zoom style={{ width: d.w, background: bg, borderRadius: 9, fontFamily: F, boxSizing: "border-box",
      padding: "9px 12px", boxShadow: "0 1px 2px rgba(34,48,60,.06)" }}>
      <div style={{ fontSize: 14, fontWeight: 650, color: ink, lineHeight: 1.2 }}>{d.label}</div>
      {d.sub && <div style={{ fontSize: 11.5, color: ink, opacity: .72, marginTop: 2, lineHeight: 1.3 }}>{d.sub}</div>}
      <Ports />
    </div>
  );
}

/** What goes wrong at a step. Still blue — it is their world. */
function Pain({ data }: NodeProps) {
  const d = data as unknown as { label: string; w: number };
  return (
    <div style={{ width: d.w, background: C.blueSoft, borderRadius: 9, fontFamily: F, boxSizing: "border-box",
      padding: "8px 11px", boxShadow: `inset 2px 0 0 ${C.blueDeep}` }}>
      <div style={{ fontSize: 12, color: C.blueDeep, opacity: .85, lineHeight: 1.35 }}>{d.label}</div>
      <Ports />
    </div>
  );
}

/** The one green container — everything we would build lives inside it. */
function Ours({ data }: NodeProps) {
  const d = data as unknown as { title: string; sub?: string; w: number; h: number };
  return (
    <div style={{ width: d.w, height: d.h, background: C.greenSoft, borderRadius: 16, fontFamily: F,
      boxShadow: `inset 0 0 0 2px ${C.greenEdge}`, paddingTop: 14, boxSizing: "border-box" }}>
      <div style={{ textAlign: "center", fontSize: 17, fontWeight: 700, color: C.greenDeep }}>{d.title}</div>
      {d.sub && <div style={{ textAlign: "center", fontSize: 12.5, color: C.greenDeep, opacity: .75, marginTop: 3 }}>{d.sub}</div>}
      <Ports />
    </div>
  );
}

function Head({ data }: NodeProps) {
  const d = data as unknown as { title: string; sub: string; w: number };
  return (
    <div style={{ width: d.w, fontFamily: F }}>
      <div style={{ fontSize: 42, fontWeight: 600, color: C.ink, letterSpacing: "-.025em", lineHeight: 1.05 }}>{d.title}</div>
      <div style={{ fontSize: 18, color: C.ink2, marginTop: 12, lineHeight: 1.5, maxWidth: 1700 }}>{d.sub}</div>
      <Ports />
    </div>
  );
}

/** A row label sitting to the left of a band. */
function Band({ data }: NodeProps) {
  const d = data as unknown as { text: string; ours?: boolean };
  return (
    <div style={{ width: 260, fontFamily: MONO, fontSize: 11.5, fontWeight: 700, letterSpacing: ".15em",
      textTransform: "uppercase", color: d.ours ? C.greenDeep : C.ink3, textAlign: "right", paddingTop: 6 }}>
      {d.text}<Ports />
    </div>
  );
}

export const flowNodeTypes = { fstep: Step, fsys: Sys, fpain: Pain, fours: Ours, fhead: Head, fband: Band, fframe: DiagramFrame };

// ── the eight steps of a job, and what serves each ──────────────────────────
const SW = 250;      // step width
const GAP = 46;      // gap between steps
const X0 = 300;      // left gutter for band labels
const x = (i: number) => X0 + i * (SW + GAP);

const STEPS = [
  { n: "01", label: "Lead arrives" },
  { n: "02", label: "Answer the call" },
  { n: "03", label: "Book the job" },
  { n: "04", label: "Dispatch a crew" },
  { n: "05", label: "Do the job" },
  { n: "06", label: "Invoice + collect" },
  { n: "07", label: "Pay the crew" },
  { n: "08", label: "Report + decide" },
];

// what they use at each step (row A), and what hurts there (row C)
const THEIRS: { i: number; sys: { label: string; sub?: string; kind?: "their" | "none" }[]; pain?: string }[] = [
  { i: 0, sys: [{ label: "Lead aggregators" }, { label: "Branded numbers", sub: "per campaign" }, { label: "Web forms", sub: "via a Google Form" }],
    pain: "Three intakes, no single view of a lead." },
  { i: 1, sys: [{ label: "CallRail", sub: "tracking · UTMs" }, { label: "Callingly", sub: "5s speed-to-lead" }, { label: "AI answering service", sub: "overflow" }, { label: "Workiz softphone", sub: "closed" }],
    pain: "No handle time and no abandoned rate — the phone gives nothing back." },
  { i: 2, sys: [{ label: "Workiz", sub: "scheduling" }],
    pain: "Manual. After-hours cannot see the real calendar, so it overbooks." },
  { i: 3, sys: [{ label: "Workiz", sub: "dispatch + routing" }],
    pain: "Jobs get lost or forgotten. Nothing handles a crew running late." },
  { i: 4, sys: [{ label: "Nothing captured", kind: "none" }],
    pain: "Never described to us. What happens on site is a blank." },
  { i: 5, sys: [{ label: "Nothing captured", kind: "none" }],
    pain: "Invoicing never came up once. No revenue reaches any report." },
  { i: 6, sys: [{ label: "Spreadsheets", sub: "several sources" }, { label: "Payroll system", sub: "never named", kind: "none" }],
    pain: "Two days every cycle, by hand, across ~55 people." },
  { i: 7, sys: [{ label: "BigQuery" }, { label: "Data Studio / Looker" }],
    pain: "Late, and not trusted. End-period beside cohorted gives a false positive." },
];

const SYS_Y = 96;    // row A top
const SPINE_Y = 470; // row B
const PAIN_Y = 620;  // row C

function mk(pre: string, ox: number, oy: number) {
  const N = (id: string, type: string, px: number, py: number, data: Record<string, unknown>, z = 3): Node =>
    ({ id: `${pre}-${id}`, type, position: { x: px + ox, y: py + oy }, draggable: false, selectable: false, zIndex: z, data });
  const E = (id: string, s: string, t: string, o: { sh?: string; th?: string; green?: boolean; dash?: boolean; text?: string } = {}): Edge => {
    const stroke = o.green ? C.greenEdge : "#A9B7C4";
    return { id: `${pre}-${id}`, source: `${pre}-${s}`, target: `${pre}-${t}`,
      sourceHandle: o.sh ?? "r", targetHandle: o.th ?? "l", type: "smoothstep", pathOptions: { borderRadius: 3 },
      style: { stroke, strokeWidth: o.green ? 2 : 1.6, strokeDasharray: o.dash ? "6 5" : undefined },
      markerEnd: { type: MarkerType.ArrowClosed, color: stroke, width: 12, height: 12 },
      label: o.text, labelShowBg: !!o.text,
      labelStyle: { fontFamily: F, fontSize: 11.5, fontWeight: 600, fill: o.green ? C.greenDeep : C.ink2 },
      labelBgStyle: { fill: "#FFFFFF" }, labelBgPadding: [5, 2] as [number, number], labelBgBorderRadius: 3, zIndex: 2 } as Edge;
  };
  return { N, E };
}

/** The spine plus rows A and C — identical in both diagrams, entirely blue. */
function theirWorld(N: ReturnType<typeof mk>["N"], E: ReturnType<typeof mk>["E"]) {
  const nodes: Node[] = [
    N("bandA", "fband", 0, SYS_Y + 6, { text: "What they use" }, 6),
    N("bandB", "fband", 0, SPINE_Y + 36, { text: "A job, step by step" }, 6),
    N("bandC", "fband", 0, PAIN_Y + 6, { text: "Where it hurts" }, 6),
  ];
  const edges: Edge[] = [];

  STEPS.forEach((s, i) => {
    nodes.push(N(`s${i}`, "fstep", x(i), SPINE_Y, { n: s.n, label: s.label, w: SW }, 4));
    if (i < STEPS.length - 1) edges.push(E(`spine${i}`, `s${i}`, `s${i + 1}`));
  });

  THEIRS.forEach((t) => {
    t.sys.forEach((sy, j) => {
      nodes.push(N(`y${t.i}_${j}`, "fsys", x(t.i), SYS_Y + j * 62, { ...sy, w: SW }));
      if (j === 0) edges.push(E(`u${t.i}`, `y${t.i}_${j}`, `s${t.i}`, { sh: "b", th: "t" }));
    });
    if (t.pain) nodes.push(N(`p${t.i}`, "fpain", x(t.i), PAIN_Y, { label: t.pain, w: SW }));
  });

  // the glue is a property of the whole left half, not of one step
  nodes.push(N("glue", "fsys", x(0), PAIN_Y + 96, {
    label: "Zapier / n8n holds steps 1 to 4 together",
    sub: "there are no native integrations, so every join is hand-built — and it breaks when Workiz renames a column",
    w: SW * 4 + GAP * 3,
  }, 5));

  return { nodes, edges };
}

export const CONTENT_W = x(7) + SW;   // right edge of the last step

// ── 1 · TODAY ───────────────────────────────────────────────────────────────
export function buildToday(ox: number, oy: number, frame = true): { nodes: Node[]; edges: Edge[] } {
  const { N, E } = mk("td", ox, oy);
  const w = theirWorld(N, E);
  const H = PAIN_Y + 200;
  return {
    nodes: [
      ...(frame ? [N("frame", "fframe", -(PAD + INNER), -(PAD + LABEL_BLOCK + INNER),
        { label: "1 · TODAY", w: CONTENT_W + 2 * (PAD + INNER), h: H + PAD * 2 + LABEL_BLOCK + INNER * 2 }, 0)] : []),
      N("h", "fhead", 0, -70, { w: 1700, title: "How a job runs today.",
        sub: "Eight steps, and the systems that serve each one. Everything here is blue because everything here is theirs — and steps five and six are empty because nobody described them to us." }, 8),
      ...w.nodes,
    ],
    edges: w.edges,
  };
}

// ── 2 · WITH TAILORMIND ─────────────────────────────────────────────────────
// Identical blue, untouched, plus a green layer underneath. Green is only ever
// ours, so "what is new" is literally "everything green".
export function buildWithUs(ox: number, oy: number, frame = true): { nodes: Node[]; edges: Edge[] } {
  const { N, E } = mk("wu", ox, oy);
  const w = theirWorld(N, E);

  const OURS_Y = PAIN_Y + 230;
  const BOX_Y = OURS_Y + 120;
  const OUT_Y = BOX_Y + 250;

  const green: Node[] = [
    N("bandD", "fband", 0, OURS_Y + 6, { text: "What we add", ours: true }, 6),

    // the read layer — one connector per system they already have
    N("conn", "fsys", x(0), OURS_Y, {
      label: "Read-only connectors + drift watch",
      sub: "one per system above · a renamed column is caught, repaired and reported",
      kind: "ours", w: SW * 4 + GAP * 3 }, 5),

    // the box
    N("box", "fours", x(1), BOX_Y, { title: "Tailormind — one server you own", sub: "your account · your data · your IP", w: SW * 4 + GAP * 3, h: 210 }, 2),
    N("copy", "fsys", x(1) + 24, BOX_Y + 62, { label: "One copy of the data", sub: "traceable to the row it came from", kind: "ours", w: 500 }),
    N("defs", "fsys", x(1) + 24, BOX_Y + 126, { label: "The definitions", sub: "a job · a lead · a source · revenue, agreed once", kind: "ours", w: 500 }),
    N("engine", "fsys", x(2) + 128, BOX_Y + 62, { label: "Workflows + agents", sub: "programs where it is predictable", kind: "ours", w: 470 }),
    N("gate", "fsys", x(2) + 128, BOX_Y + 126, { label: "The write gate", sub: "draft first · nothing writes back without it", kind: "ours", w: 470 }),

    // what comes out, each hanging under the step it serves
    N("o2", "fsys", x(1), OUT_Y, { label: "Call visibility", sub: "only as far as the tracking layer reaches", kind: "ours", w: SW }),
    N("o3", "fsys", x(2), OUT_Y, { label: "Real availability", sub: "no overbooking · after-hours honest", kind: "ours", w: SW }),
    N("o4", "fsys", x(3), OUT_Y, { label: "Running-late handoff", sub: "drafts it, a person sends it", kind: "ours", w: SW }),
    N("o7", "fsys", x(6), OUT_Y, { label: "The commission run", sub: "computed · exceptions flagged · you approve", kind: "ours", w: SW }),
    N("o8", "fsys", x(7), OUT_Y, { label: "Ask in plain language", sub: "and boards that compare like with like", kind: "ours", w: SW }),
  ];

  const greenEdges: Edge[] = [
    // we READ from their systems
    ...[0, 1, 2, 3].map((i) => E(`r${i}`, `y${i}_0`, "conn", { sh: "b", th: "t", green: true })),
    E("r6", "y6_0", "conn", { sh: "b", th: "r", green: true }),
    E("r7", "y7_0", "conn", { sh: "b", th: "r", green: true }),
    E("in", "conn", "box", { sh: "b", th: "t", green: true, text: "into one model" }),
    // and we give back, at the step it belongs to
    E("g2", "box", "o2", { sh: "b", th: "t", green: true, dash: true, text: "if reachable" }),
    E("g3", "box", "o3", { sh: "b", th: "t", green: true }),
    E("g4", "box", "o4", { sh: "b", th: "t", green: true }),
    E("g7", "box", "o7", { sh: "b", th: "t", green: true }),
    E("g8", "box", "o8", { sh: "b", th: "t", green: true }),
    // the outputs serve their step
    E("b2", "o2", "s1", { sh: "t", th: "b", green: true }),
    E("b3", "o3", "s2", { sh: "t", th: "b", green: true }),
    E("b4", "o4", "s3", { sh: "t", th: "b", green: true }),
    E("b7", "o7", "s6", { sh: "t", th: "b", green: true }),
    E("b8", "o8", "s7", { sh: "t", th: "b", green: true }),
  ];

  const H = OUT_Y + 130;
  return {
    nodes: [
      ...(frame ? [N("frame", "fframe", -(PAD + INNER), -(PAD + LABEL_BLOCK + INNER),
        { label: "2 · WITH TAILORMIND", w: CONTENT_W + 2 * (PAD + INNER), h: H + PAD * 2 + LABEL_BLOCK + INNER * 2 }, 0)] : []),
      N("h", "fhead", 0, -70, { w: 1700, title: "The same eight steps. Everything green is new.",
        sub: "The blue is untouched — they keep every system they already pay for, and the process does not change. We read from what exists, put it in one model they own, and hand something back at five specific steps. Steps five and six stay empty until they tell us what happens there." }, 8),
      ...w.nodes,
      ...green,
    ],
    edges: [...w.edges, ...greenEdges],
  };
}
